import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { brl, fdate, fdatetime, todayISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import NovaVendaModal from "@/components/NovaVendaModal";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, Search, Pencil, RefreshCw, Loader2, Trash2 } from "lucide-react";

const EMPTY = { name: "", phone: "", cpf: "", birth_date: "", plan_id: "", due_date: "", professor: "", notes: "" };

function dueBadge(s) {
  if (!s.due_date) return <span className="text-muted-foreground">—</span>;
  const today = todayISO();
  if (s.due_date < today)
    return <Badge variant="outline" className="border-red-500/40 text-red-500">Vencido {fdate(s.due_date)}</Badge>;
  const days = Math.ceil((new Date(s.due_date) - new Date(today)) / 86400000);
  if (days <= 7)
    return <Badge variant="outline" className="border-amber-500/40 text-amber-500">{days}d restantes</Badge>;
  return <Badge variant="outline" className="border-emerald-500/40 text-emerald-500">{fdate(s.due_date)}</Badge>;
}

function StudentDialog({ open, onOpenChange, student }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { data: plans } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await api.get("/plans")).data,
  });

  useEffect(() => {
    if (open) setForm(student ? { ...EMPTY, ...student, birth_date: student.birth_date || "", plan_id: student.plan_id || "", due_date: student.due_date || "" } : EMPTY);
  }, [open, student]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }
    setSaving(true);
    const payload = { ...form, plan_id: form.plan_id || null, due_date: form.due_date || null, birth_date: form.birth_date || null };
    try {
      if (student) await api.put(`/students/${student.id}`, payload);
      else await api.post("/students", payload);
      toast.success(student ? "Aluno atualizado" : "Aluno cadastrado");
      qc.invalidateQueries();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="student-dialog">
        <DialogHeader>
          <DialogTitle>{student ? "Editar aluno" : "Novo aluno"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5">
            <Label>Nome *</Label>
            <Input data-testid="student-name" value={form.name} onChange={set("name")} />
          </div>
          <div className="space-y-1.5">
            <Label>Telefone</Label>
            <Input data-testid="student-phone" value={form.phone} onChange={set("phone")} />
          </div>
          <div className="space-y-1.5">
            <Label>CPF</Label>
            <Input data-testid="student-cpf" value={form.cpf} onChange={set("cpf")} />
          </div>
          <div className="space-y-1.5">
            <Label>Nascimento</Label>
            <Input type="date" data-testid="student-birth" value={form.birth_date} onChange={set("birth_date")} />
          </div>
          <div className="space-y-1.5">
            <Label>Professor</Label>
            <Input data-testid="student-professor" value={form.professor} onChange={set("professor")} />
          </div>
          <div className="space-y-1.5">
            <Label>Plano</Label>
            <Select value={form.plan_id} onValueChange={(v) => setForm((f) => ({ ...f, plan_id: v }))}>
              <SelectTrigger data-testid="student-plan">
                <SelectValue placeholder="Sem plano" />
              </SelectTrigger>
              <SelectContent>
                {(plans || []).filter((p) => p.active).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} — {brl(p.price)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Vencimento</Label>
            <Input type="date" data-testid="student-due" value={form.due_date} onChange={set("due_date")} />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label>Observações</Label>
            <Textarea data-testid="student-notes" value={form.notes} onChange={set("notes")} rows={2} />
          </div>
        </div>
        <Button data-testid="student-save" onClick={save} disabled={saving} className="mt-2 w-full h-11">
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Salvar
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function Alunos() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [renewStudent, setRenewStudent] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const removeStudent = async () => {
    try {
      await api.delete(`/students/${detailId}`);
      toast.success("Aluno excluído");
      setDeleteOpen(false);
      setDetailId(null);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const { data: students, isLoading } = useQuery({
    queryKey: ["students", q],
    queryFn: async () => (await api.get("/students", { params: { q } })).data,
  });
  const { data: detail } = useQuery({
    queryKey: ["student", detailId],
    queryFn: async () => (await api.get(`/students/${detailId}`)).data,
    enabled: !!detailId,
  });

  useEffect(() => {
    const t = setTimeout(() => setParams(q ? { q } : {}), 300);
    return () => clearTimeout(t);
  }, [q, setParams]);

  return (
    <div className="space-y-6" data-testid="alunos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Alunos</h1>
          <p className="text-sm text-muted-foreground">{(students || []).length} alunos cadastrados</p>
        </div>
        <Button data-testid="new-student-btn" onClick={() => { setEditing(null); setDialogOpen(true); }}>
          <Plus className="mr-1 h-4 w-4" /> Novo aluno
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="students-search" placeholder="Buscar por nome, telefone ou CPF..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          {isLoading ? (
            <div className="p-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
          ) : (
            <Table data-testid="students-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Telefone</TableHead>
                  <TableHead>Plano</TableHead>
                  <TableHead>Vencimento</TableHead>
                  <TableHead>Professor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(students || []).map((s) => (
                  <TableRow key={s.id} className="cursor-pointer" onClick={() => setDetailId(s.id)} data-testid={`student-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-muted-foreground">{s.phone || "—"}</TableCell>
                    <TableCell>{s.plan_name || <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell>{dueBadge(s)}</TableCell>
                    <TableCell className="text-muted-foreground">{s.professor || "—"}</TableCell>
                  </TableRow>
                ))}
                {(students || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                      Nenhum aluno encontrado.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <StudentDialog open={dialogOpen} onOpenChange={setDialogOpen} student={editing} />

      <Sheet open={!!detailId} onOpenChange={() => setDetailId(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="student-detail-sheet">
          {detail && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-xl">{detail.student.name}</SheetTitle>
              </SheetHeader>
              <div className="mt-4 space-y-6">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="text-xs text-muted-foreground">Telefone</div>{detail.student.phone || "—"}</div>
                  <div><div className="text-xs text-muted-foreground">CPF</div>{detail.student.cpf || "—"}</div>
                  <div><div className="text-xs text-muted-foreground">Plano</div>{detail.student.plan_name || "—"}</div>
                  <div><div className="text-xs text-muted-foreground">Vencimento</div>{dueBadge(detail.student)}</div>
                  <div><div className="text-xs text-muted-foreground">Professor</div>{detail.student.professor || "—"}</div>
                  <div><div className="text-xs text-muted-foreground">Total gasto</div><span className="num font-semibold text-emerald-500">{brl(detail.total_spent)}</span></div>
                </div>
                {detail.student.notes && (
                  <div className="rounded-lg bg-muted/60 p-3 text-sm text-muted-foreground">{detail.student.notes}</div>
                )}
                <div className="flex gap-2">
                  <Button
                    data-testid="renew-plan-btn"
                    className="bg-emerald-600 text-white hover:bg-emerald-500"
                    onClick={() => setRenewStudent({ id: detail.student.id, name: detail.student.name })}
                  >
                    <RefreshCw className="mr-1 h-4 w-4" /> Renovar / vender
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="edit-student-btn"
                    onClick={() => { setEditing(detail.student); setDialogOpen(true); }}
                  >
                    <Pencil className="mr-1 h-4 w-4" /> Editar
                  </Button>
                  {(user.role === "admin" || user.role === "gerente") && (
                    <Button
                      variant="outline"
                      className="border-red-500/40 text-red-500 hover:bg-red-500/10"
                      data-testid="delete-student-btn"
                      onClick={() => setDeleteOpen(true)}
                    >
                      <Trash2 className="mr-1 h-4 w-4" /> Excluir
                    </Button>
                  )}
                </div>
                <div>
                  <div className="mb-2 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    Histórico financeiro
                  </div>
                  <div className="divide-y divide-border rounded-xl border border-border">
                    {detail.sales.length === 0 && (
                      <div className="p-4 text-sm text-muted-foreground">Nenhuma compra registrada.</div>
                    )}
                    {detail.sales.map((s) => (
                      <div key={s.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`student-sale-${s.id}`}>
                        <div>
                          <div>{s.items.map((i) => i.name).join(", ")}</div>
                          <div className="text-xs text-muted-foreground">{fdatetime(s.created_at)} · {s.payment_method_name}</div>
                        </div>
                        <span className="num font-semibold text-emerald-500">{brl(s.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-student-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir aluno?</AlertDialogTitle>
            <AlertDialogDescription>
              O aluno será excluído (exclusão lógica — o histórico financeiro e as vendas dele continuam registrados para auditoria).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-student-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={removeStudent} data-testid="delete-student-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NovaVendaModal
        open={!!renewStudent}
        onOpenChange={() => setRenewStudent(null)}
        initialStudent={renewStudent}
        onSaved={() => setDetailId(null)}
      />
    </div>
  );
}
